import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faPhone, 
  faEnvelope,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { 
  faFacebookF, 
  faInstagram, 
  faWhatsapp 
} from '@fortawesome/free-brands-svg-icons';
import logo from '../../assets/fexlogistics-logo.jpg';

const Footer = () => {
  const [isCareerOpen, setIsCareerOpen] = useState(false);

  const toggleCareerDropdown = () => {
    setIsCareerOpen(!isCareerOpen);
  };

  return (
    <footer className="bg-black text-white pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-wrap justify-between">
          {/* Logo and Description */}
          <div className="w-full md:w-1/4 mb-8 md:mb-0">
            <Link to="/" className="flex items-center mb-4">
              <img src={logo} alt="Fex Logistics Logo" className="h-12" />
            </Link>
            <p className="text-gray-400 mb-6 max-w-xs">
              Dynamic and innovative logistics solutions tailored for businesses of all sizes.
            </p>
            
            {/* Social Media Links */}
            <div className="flex space-x-4">
              <a 
                href="#" 
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-red-600 transition-colors duration-300 text-white"
                aria-label="Facebook"
              >
                <FontAwesomeIcon icon={faFacebookF} className="text-white" />
              </a>
              <a 
                href="https://www.instagram.com/fexlogistics_ng?utm_source=ig_web_button_share_sheet&igsh=c3U4b3BtZTU1NGh2" 
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-red-600 transition-colors duration-300 text-white"
                aria-label="Instagram"
              >
                <FontAwesomeIcon icon={faInstagram} className="text-white" />
              </a>
              <a 
                href="https://wa.me/2347046674299" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-red-600 transition-colors duration-300 text-white"
                aria-label="WhatsApp"
                title="WhatsApp: 234-7046674299"
              >
                <FontAwesomeIcon icon={faWhatsapp} className="text-white" />
              </a>
            </div>
          </div>
          
          {/* Quick Links */}
          <div className="w-full md:w-1/5 mb-8 md:mb-0">
            <h4 className="text-xl font-bold mb-6 relative pb-3 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-12 after:h-0.5 after:bg-red-600">
              Quick Links
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/quote" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Quote
                </Link>
              </li>
              <li>
                <Link to="/location" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Location
                </Link>
              </li>
              <li className="relative">
                <div className="relative">
                  <button
                    onClick={toggleCareerDropdown}
                    className="flex items-center text-white hover:text-red-600 transition-colors duration-300 focus:outline-none"
                  >
                    <span>Career</span>
                    <FontAwesomeIcon 
                      icon={isCareerOpen ? faChevronUp : faChevronDown} 
                      className="ml-2 text-xs"
                    />
                  </button>
                  
                  {isCareerOpen && (
                    <ul className="mt-2 space-y-2 bg-black border border-gray-800 rounded-md p-3 absolute left-0 z-10 w-56 shadow-lg">
                      <li>
                        <Link 
                          to="/career/rider" 
                          className="block text-white hover:text-red-600 transition-colors duration-300 no-underline py-1"
                          onClick={() => setIsCareerOpen(false)}
                        >
                          Become a Rider
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/career/driver" 
                          className="block text-white hover:text-red-600 transition-colors duration-300 no-underline py-1"
                          onClick={() => setIsCareerOpen(false)}
                        >
                          Driver
                        </Link>
                      </li>
                      <li>
                        <Link 
                          to="/career/partner" 
                          className="block text-white hover:text-red-600 transition-colors duration-300 no-underline py-1"
                          onClick={() => setIsCareerOpen(false)}
                        >
                          Partner
                        </Link>
                      </li>
                    </ul>
                  )}
                </div>
              </li>
              <li>
                <Link to="/login" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Tracking
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Our Services */}
          <div className="w-full md:w-1/5 mb-8 md:mb-0">
            <h4 className="text-xl font-bold mb-6 relative pb-3 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-12 after:h-0.5 after:bg-red-600">
              Our Services
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/quote/express-international" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Express International
                </Link>
              </li>
              <li>
                <Link to="/quote/cargo-shipping" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Cargo Shipping
                </Link>
              </li>
              <li>
                <Link to="/quote/ecommerce" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  E-commerce
                </Link>
              </li>
              <li>
                <Link to="/quote/local-delivery" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Local Delivery
                </Link>
              </li>
              <li>
                <Link to="/quote/b2b-logistics" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  B2B Logistics
                </Link>
              </li>
              <li>
                <Link to="/quote/warehousing" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Warehousing
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Legal Links */}
          <div className="w-full md:w-1/5 mb-8 md:mb-0">
            <h4 className="text-xl font-bold mb-6 relative pb-3 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-12 after:h-0.5 after:bg-red-600">
              Legal
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-white hover:text-red-600 transition-colors duration-300 hover:pl-1 inline-block no-underline">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Copyright Notice */}
        <div className="border-t border-gray-800 mt-12 pt-6 text-center">
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Fex Logistics. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;